// Scenario 4: cancelled_grace_period_gb
// UK tenant, PROFESSIONAL x 5 seats, GBP monthly. User cancelled 2 days ago.
// cancelAtPeriodEnd: true, currentPeriodEnd 12 days out.

import {
  buildConsumptionEvents,
  buildFeatureBreakdown
} from './ai-credit-usage-fixtures';
import { Scenario, TENANT_ID_GB, daysFromNow } from './scenario.types';
import { SHARED_PLANS, SHARED_TOP_UP_PACKS } from './shared-plans';

const PRICE_PER_SEAT_MINOR = 3200;   // PROFESSIONAL GBP monthly
const SEATS = 5;
const SUBTOTAL_MINOR = PRICE_PER_SEAT_MINOR * SEATS;
const TOTAL_MINOR = SUBTOTAL_MINOR;

export const cancelledGracePeriodGb: Scenario = {
  name: 'cancelled_grace_period_gb',
  subscription: {
    id: 'sub_cancelled_grace_period_gb',
    stripeSubscriptionId: null,
    tenantId: TENANT_ID_GB,
    status: 'active',
    tier: 'PROFESSIONAL',
    billingPeriod: 'monthly',
    currency: 'GBP',
    countryCode: 'GB',
    seats: SEATS,
    pricePerSeatMinor: PRICE_PER_SEAT_MINOR,
    subtotalMinor: SUBTOTAL_MINOR,
    discountMinor: 0,
    taxMinor: 0,
    totalMinor: TOTAL_MINOR,
    appliedCoupon: null,
    currentPeriodStart: daysFromNow(-18),
    currentPeriodEnd: daysFromNow(12),
    cancelAtPeriodEnd: true,
    canceledAt: daysFromNow(-2),
    pastDueSince: null,
    gracePeriodEndsAt: null
  },
  plans: SHARED_PLANS,
  invoices: [
    {
      id: 'inv_gb_001',
      number: 'INV-2026-0030',
      status: 'paid',
      issuedAt: daysFromNow(-18),
      paidAt: daysFromNow(-18),
      periodStart: daysFromNow(-18),
      periodEnd: daysFromNow(12),
      currency: 'GBP',
      subtotalMinor: SUBTOTAL_MINOR,
      discountMinor: 0,
      taxMinor: 3200,
      taxRate: 20,
      taxBasis: 'exclusive',
      totalMinor: TOTAL_MINOR + 3200,
      hostedInvoiceUrl: 'https://invoice.stripe.com/mock/inv_gb_001',
      invoicePdfUrl: 'https://invoice.stripe.com/mock/inv_gb_001.pdf',
      lineItems: [
        {
          description: 'Professional plan x 5 seats',
          kind: 'subscription',
          quantity: SEATS,
          unitAmountMinor: PRICE_PER_SEAT_MINOR,
          amountMinor: SUBTOTAL_MINOR,
          periodStart: daysFromNow(-18),
          periodEnd: daysFromNow(12)
        }
      ]
    }
  ],
  receipts: [
    {
      id: 'rcpt_gb_001',
      chargedAt: daysFromNow(-18),
      amountMinor: TOTAL_MINOR + 3200,
      currency: 'GBP',
      description: 'Solo subscription renewal',
      invoiceId: 'inv_gb_001',
      topUpPackId: null
    }
  ],
  paymentMethod: {
    id: 'pm_gb_001',
    type: 'card',
    brand: 'mastercard',
    last4: '5454',
    expMonth: 7,
    expYear: 2028,
    isDefault: true
  },
  aiCreditBalance: {
    tenantId: TENANT_ID_GB,
    // S8: static ISO dates so the toast dedup key is stable across midnight UTC.
    billingCycleStart: '2026-05-05T00:00:00.000Z',
    billingCycleEnd: '2026-06-04T00:00:00.000Z',
    // Per #08 spec: 4,000 allocated / 3,850 used / ~3.75% remaining -> fires 5%
    // threshold toast on first balance emission.
    allocatedCredits: 4000,
    usedCredits: 3850,
    reservedCredits: 0,
    topUpBalance: 0,
    remainingCredits: 4000 - 3850,
    basicTurnsAllocated: 5000,
    basicTurnsUsed: 1100
  },
  topUpPacks: SHARED_TOP_UP_PACKS,
  consumptionEvents: buildConsumptionEvents({
    cycleAllocated: 4000,
    currentUsed: 3850,
    topUpBalance: 0,
    idPrefix: 'cancelled_gb',
    seeds: [
      { featureKey: 'ai_chat', creditsConsumed: 400, modelTier: 'premium', hoursAgo: 100, requestSummary: "Chat: 'team kickoff'" },
      { featureKey: 'document_extraction', creditsConsumed: 380, modelTier: 'premium', hoursAgo: 88, requestSummary: 'Extract supplier invoices' },
      { featureKey: 'ai_chat', creditsConsumed: 360, modelTier: 'premium', hoursAgo: 76, requestSummary: "Chat: 'pipeline review'" },
      { featureKey: 'document_extraction', creditsConsumed: 320, modelTier: 'premium', hoursAgo: 60, requestSummary: 'Extract delivery note' },
      { featureKey: 'ai_chat', creditsConsumed: 280, modelTier: 'premium', hoursAgo: 48, requestSummary: "Chat: 'forecast question'" },
      { featureKey: 'summarization', creditsConsumed: 220, modelTier: 'basic', hoursAgo: 36, requestSummary: 'Summarize finance email' },
      { featureKey: 'image_generation', creditsConsumed: 360, modelTier: 'premium', hoursAgo: 28, requestSummary: 'Generate campaign asset' },
      { featureKey: 'ai_chat', creditsConsumed: 320, modelTier: 'premium', hoursAgo: 20, requestSummary: "Chat: 'invoice question'" },
      { featureKey: 'document_extraction', creditsConsumed: 380, modelTier: 'premium', hoursAgo: 12, requestSummary: 'Extract PO line items' },
      { featureKey: 'ai_chat', creditsConsumed: 280, modelTier: 'premium', hoursAgo: 6, requestSummary: "Chat: 'support reply'" },
      { featureKey: 'summarization', creditsConsumed: 190, modelTier: 'basic', hoursAgo: 1, requestSummary: 'Summarize transcript' }
    ]
  }),
  featureUsageBreakdown: [],
  topUpHistory: []
};

(cancelledGracePeriodGb.featureUsageBreakdown as unknown) = buildFeatureBreakdown(
  cancelledGracePeriodGb.consumptionEvents ?? []
);
